import { useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import type { IconType } from 'react-icons';
import {
  LuArrowRight,
  LuCalendar,
  LuLightbulb,
  LuShieldCheck,
  LuStar,
  LuUser,
  LuUsers,
} from 'react-icons/lu';

import CareersNoticeModal from '../app/components/CareersNoticeModal';
import GradientCTA from '../app/components/GradientCTA';
import HeroAnimated from '../app/components/HeroAnimated';
import ImpactResultsSection from '../app/components/ImpactResultsSection';
import RevealSection from '../app/components/RevealSection';
import StaggeredScrollReveal from '../app/components/StaggeredScrollReveal';
import SbrIcon from '../app/components/SbrIcon';
import { SBR_IMPACT_STATS } from '../data/sbrImpactStats';

const ABOUT_HEADER_IMG = `${process.env.PUBLIC_URL}/images/aboutusheader.png`;

const heroFeatures: { title: string; body: string; Icon: IconType }[] = [
  {
    title: 'AI Precision Candidate Discovery',
    body: 'Surfacing the highest-fit candidates from across the market.',
    Icon: LuUsers,
  },
  {
    title: 'Human Expertise',
    body: 'Domain experts validate, engage, and deliver.',
    Icon: LuUser,
  },
];

type ValueTone = 'teal' | 'blue' | 'purple';

const values: { title: string; body: string; Icon: IconType; tone: ValueTone }[] = [
  { title: 'Integrity', body: 'We do what\u2019s right. Always.', Icon: LuShieldCheck, tone: 'teal' },
  { title: 'Excellence', body: 'We raise the bar and deliver exceptional results.', Icon: LuStar, tone: 'blue' },
  { title: 'Partnership', body: 'We succeed when our clients succeed.', Icon: LuUsers, tone: 'purple' },
  { title: 'Innovation', body: 'We embrace technology to solve complex problems.', Icon: LuLightbulb, tone: 'teal' },
];

const LEADERS_IMG = `${process.env.PUBLIC_URL}/images/leaders`;

type Leader = {
  name: string;
  title: string;
  bio: string;
  imageSlug: string;
  linkedIn?: string;
};

const leaders: Leader[] = [
  {
    name: 'John Light',
    title: 'Co-Founder & CEO',
    bio: 'Career recruiting executive.',
    imageSlug: 'john',
    linkedIn: 'https://www.linkedin.com/in/johndlight/',
  },
  {
    name: 'Nathan Deily',
    title: 'Co-Founder & VP, Service Delivery',
    bio: 'Veteran of Corporate HR in technology, aerospace & defense.',
    imageSlug: 'nathan',
    linkedIn: 'https://www.linkedin.com/in/nathan-deily/',
  },
  {
    name: 'Sam Johnston',
    title: 'Co-Founder & VP Marketing',
    bio: 'Global business leader with expertise in talent, technology, and growth.',
    imageSlug: 'sam',
    linkedIn: 'https://www.linkedin.com/in/sam-johnston/',
  },
  {
    name: 'Lahirimahasaya Bestha',
    title: 'Lead AI Developer',
    bio: 'Driving force behind SBR2TH AI.',
    imageSlug: 'lahiri',
    linkedIn: 'https://www.linkedin.com/in/lahirimahasaya-bestha-6270a812b/',
  },
  {
    name: 'Scott Forsyth',
    title: 'Technical Advisor',
    bio: 'Veteran software engineering leader, CTO and Solutions Architect.',
    imageSlug: 'scott',
    linkedIn: 'https://www.linkedin.com/in/scottjforsyth/',
  },
];

export default function AboutPage() {
  const [showCareersModal, setShowCareersModal] = useState(false);

  return (
    <>
      <section className="about-hero text-white">
        <div className="about-hero__main">
          <div className="about-hero__media">
            <img
              src={ABOUT_HEADER_IMG}
              alt="Diverse team collaborating around a laptop in a modern office"
              className="about-hero__img"
              width={1920}
              height={1080}
              decoding="async"
              fetchPriority="high"
            />
            <div className="about-hero__fade" aria-hidden />
          </div>
          <Container className="about-hero__content py-4 pt-lg-5 pb-3 pb-lg-4">
            <Row className="about-hero__row gy-4 gy-lg-5">
              <Col lg={7} className="about-hero__copy-col">
                <HeroAnimated className="about-hero__animated">
                  <div className="about-hero__headline">
                    <p data-hero-item className="about-hero__eyebrow mb-2">
                      About SBR2TH
                    </p>
                    <h1 data-hero-item className="display-5 fw-bold mb-0">
                      We exist to help companies build{' '}
                      <span className="about-hero__gradient-stronger">stronger</span>{' '}
                      <span className="about-hero__gradient-teams">teams.</span>
                    </h1>
                  </div>

                  <div className="about-hero__body">
                    <p data-hero-item className="lead text-white-50 mb-0 pe-lg-2">
                      SBR2TH combines the power of AI with deep human expertise to deliver high-caliber talent for your
                      hardest-to-fill roles, quality candidates, faster.
                    </p>
                  </div>

                  <Row data-hero-item className="about-hero__features">
                    {heroFeatures.map((f) => (
                      <Col sm={6} key={f.title}>
                        <div className="solution-hero-feature">
                          <div className="solution-hero-feature__icon">
                            <SbrIcon icon={f.Icon} size={24} />
                          </div>
                          <div className="solution-hero-feature__copy">
                            <div className="solution-hero-feature__title">{f.title}</div>
                            <p className="solution-hero-feature__body">{f.body}</p>
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </HeroAnimated>
              </Col>
            </Row>
          </Container>
        </div>
      </section>

      <section className="about-mission-values bg-sbr-light py-5 py-lg-6">
        <RevealSection>
          <Container>
            <Row className="about-mission-values__row g-0">
              <Col lg={5} className="about-mission-values__mission">
                <p className="about-mission-values__eyebrow about-mission-values__eyebrow--teal">Our Mission</p>
                <h2 className="about-mission-values__title sbr-section__title sbr-section__title--accent fw-bold mb-3">
                  Hire critical talent. <span className="about-mission-values__accent">Quality candidates.</span> Faster.
                </h2>
                <p className="about-mission-values__lead mb-0">
                  We partner with companies to fill mission-critical roles that drive growth and performance. Our AI-powered
                  platform and expert recruiters work together to deliver exceptional results, every time.
                </p>
              </Col>
              <Col lg={7} className="about-mission-values__values">
                <p className="about-mission-values__eyebrow">Our Values</p>
                <StaggeredScrollReveal className="about-mission-values__grid">
                  <Row className="g-2 g-lg-3">
                    {values.map((v) => (
                      <Col key={v.title} xs={6} lg={3} className="about-mission-values__cell">
                        <div className={`about-value about-value--${v.tone}`} data-stagger-item>
                          <div className="about-value__icon-wrap">
                            <SbrIcon icon={v.Icon} size={26} />
                          </div>
                          <h3 className="about-value__title">{v.title}</h3>
                          <p className="about-value__body">{v.body}</p>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </StaggeredScrollReveal>
              </Col>
            </Row>
          </Container>
        </RevealSection>
      </section>

      <section className="about-leadership bg-sbr-light py-5 py-lg-6">
        <RevealSection>
          <Container>
            <div className="sbr-section-header text-center mb-5">
              <h2 className="sbr-section__title fw-bold">Leadership That Understands Hiring</h2>
              <p className="sbr-section__lead mb-0 mx-auto">
                Our leadership team brings decades of experience in talent acquisition, technology, and building
                high-performance organizations.
              </p>
            </div>
            <Row className="about-leadership__grid g-3 g-lg-4 justify-content-center">
              {leaders.map((l) => (
                <Col sm={6} lg={4} key={l.name}>
                  <article className="about-leader-card">
                    <div className="about-leader-card__media">
                      <picture>
                        <source
                          srcSet={`${LEADERS_IMG}/${l.imageSlug}.webp`}
                          type="image/webp"
                        />
                        <img
                          src={`${LEADERS_IMG}/${l.imageSlug}.jpg`}
                          alt={l.name}
                          className="about-leader-card__photo"
                          width={320}
                          height={368}
                          loading="lazy"
                          decoding="async"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            e.currentTarget
                              .closest('.about-leader-card__media')
                              ?.classList.add('about-leader-card__media--placeholder');
                          }}
                        />
                      </picture>
                    </div>
                    <div className="about-leader-card__body">
                      <h3 className="about-leader-card__name">{l.name}</h3>
                      <p className="about-leader-card__title">{l.title}</p>
                      <p className="about-leader-card__bio">{l.bio}</p>
                      {l.linkedIn ? (
                        <a
                          className="about-leader-card__linkedin"
                          href={l.linkedIn}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`${l.name} on LinkedIn`}
                        >
                          <i className="bi bi-linkedin" aria-hidden />
                        </a>
                      ) : null}
                    </div>
                  </article>
                </Col>
              ))}
            </Row>
            <div className="text-center mt-4 mt-lg-5">
              <button
                type="button"
                className="about-leadership__careers btn btn-link p-0 border-0"
                onClick={() => setShowCareersModal(true)}
              >
                View Our Careers
                <SbrIcon icon={LuArrowRight} className="ms-2" size={18} />
              </button>
            </div>
          </Container>
        </RevealSection>
      </section>

      <ImpactResultsSection
        reveal
        eyebrow="Real Impact"
        title={
          <>
            Delivering results that <span className="text-gradient-teal-blue">drive growth.</span>
          </>
        }
        lead="We measure our success by the impact we create for our clients."
        cta={{ to: '/book-a-call', label: 'Book a 30 Min Call' }}
        stats={SBR_IMPACT_STATS}
      />

      <GradientCTA
        icon={<SbrIcon icon={LuCalendar} size={26} />}
        title={
          <>
            <div>Let&apos;s build your next great team.</div>
            <div className="sbr-cta__subtitle fw-normal opacity-90 mt-2 mb-0">
              Schedule a 30-minute call with our team to get started.
            </div>
          </>
        }
      />

      <CareersNoticeModal show={showCareersModal} onHide={() => setShowCareersModal(false)} />
    </>
  );
}
